// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import updateXCheckoutState from "@awc/App/Handlers/Checkout/UpdateLocalState";
import { CustomAppState } from "@awc/App/Store/Reducers";
import { BillingFormId } from "@awc/XDL/constants";
import { xValidateOnSubmit } from "@awc/XDL/Utilities";
import { scrollToFirstFormError } from "@insite/client-framework/Common/CustomerSelectors";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import setAddressBookOverlayIsOpen from "@insite/client-framework/Store/Components/AddressBookOverlay/Handlers/SetAddressBookOverlayIsOpen";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentAccountState } from "@insite/client-framework/Store/Data/Accounts/AccountsSelector";
import loadCurrentAccount from "@insite/client-framework/Store/Data/Accounts/Handlers/LoadCurrentAccount";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import {
    AddressFieldDisplayCollectionModel,
    BillToModel,
    CountryModel,
} from "@insite/client-framework/Types/ApiModels";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import BillToSelector, { BillToSelectorStyles } from "@insite/content-library/Components/BillToSelector";
import CustomerAddressForm, { CustomerAddressFormStyles } from "@insite/content-library/Components/CustomerAddressForm";
import { CheckoutShippingFormContext } from "@insite/content-library/Pages/CheckoutShippingPage";
import BillingAddressForm, {
    BillingAddressFormStyles,
} from "@insite/content-library/Widgets/CheckoutShipping/BillingAddressForm";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps, CheckboxProps } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupComponentProps } from "@insite/mobius/CheckboxGroup/CheckboxGroup";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

interface OwnProps {
    address: BillToModel;
    onChange: (address: BillToModel, isEdit: boolean) => void;
    countries: CountryModel[];
    addressFieldDisplayCollection: AddressFieldDisplayCollectionModel;
    extendedStyles?: BillingAddressStyles;
}

const mapStateToProps = (state: CustomAppState) => {
    const { cartId } = state.pages.checkoutShipping;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const cart = cartState.value;
    return {
        currentUserIsGuest: getCurrentUserIsGuest(state),
        isBillingAddressUpdateRequired: state.pages.checkoutShipping.isBillingAddressUpdateRequired,
        cart,
        cartBillTo: getBillToState(state, cart?.billToId)?.value,
        // XNG-Change
        billToEditMode: state.xCheckout.addresses.billToEditMode,
        billingAddressFormState: state.pages.checkoutShipping.billingAddressFormState?.address,
        isSubscribedFromState: state.xCheckout.addresses.isSubscribed,
        account: getCurrentAccountState(state)?.value,
    };
};

const mapDispatchToProps = {
    setAddressBookOverlayIsOpen,
    // XNG-Change
    updateXCheckoutState,
    loadCurrentAccount,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    ThemeProps<BaseTheme>;

export interface BillingAddressStyles {
    container?: GridContainerProps;
    headingGridItem?: GridItemProps;
    headingText?: TypographyPresentationProps;
    selectSavedAddressLink?: LinkPresentationProps;
    addressBookModal?: ModalPresentationProps;
    billToSelector?: BillToSelectorStyles;
    addressBookModalButtonsWrapper?: InjectableCss;
    addressBookModalCancelButton?: ButtonPresentationProps;
    addressDisplayAndFormGridItem?: GridItemProps;
    addressForm?: BillingAddressFormStyles;
    addressDisplay?: AddressInfoDisplayStyles;
    editAddressForm?: CustomerAddressFormStyles;
    // XNG-Change
    loadingOverlay?: LoadingOverlayProps;
    saveWrapper?: InjectableCss;
    saveButton?: ButtonPresentationProps;
    change?: LinkPresentationProps;
    subscribeCheckbox?: CheckboxPresentationProps;
    manageSubscriptionsCheckboxGroup?: CheckboxGroupComponentProps;
    manageSubscriptionsCheckbox?: CheckboxProps;
}

export const billingAddressStyles: BillingAddressStyles = {
    container: { gap: 24 },
    headingGridItem: {
        width: 12,
        css: css`
            align-items: center;
        `,
    },
    headingText: {
        variant: "h5",
        css: css`
            margin-bottom: 0;
        `,
    },
    selectSavedAddressLink: {
        css: css`
            margin-left: 20px;
        `,
    },
    addressBookModal: { sizeVariant: "medium" },
    addressBookModalButtonsWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            margin-top: 1rem;
        `,
    },
    addressBookModalCancelButton: { variant: "secondary" },
    addressDisplayAndFormGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
};

const BillingAddress = ({
    address,
    onChange,
    countries,
    addressFieldDisplayCollection,
    extendedStyles,
    currentUserIsGuest,
    toaster,
    isBillingAddressUpdateRequired,
    cart,
    cartBillTo,
    theme,
    setAddressBookOverlayIsOpen,
    // XNG-Change
    billToEditMode,
    updateXCheckoutState,
    billingAddressFormState,
    isSubscribedFromState,
    account,
    loadCurrentAccount,
}: Props) => {
    const [isCustomerSelectorModalOpen, setIsCustomerSelectorModalOpen] = useState(false);
    const [isEditAddressModalOpen, setIsEditAddressModalOpen] = useState(false);
    const [addressBeingEdited, setAddressBeingEdited] = useState<BillToModel | undefined>(undefined);
    // XNG-Change
    const [initialIsSubscribed, setInitialIsSubscribed] = useState(!!account?.isSubscribed);
    // XNG-Change
    const context = useContext(CheckoutShippingFormContext);

    useEffect(() => {
        if (addressBeingEdited) {
            setIsEditAddressModalOpen(true);
        }
    }, [addressBeingEdited]);

    // XNG-Change
    useEffect(() => {
        if (!account) {
            loadCurrentAccount();
        }
    }, [account]);

    useEffect(() => {
        if (account) {
            setInitialIsSubscribed(!!account.isSubscribed);
        }
    }, [account?.isSubscribed]);

    useEffect(() => {
        if (isSubscribedFromState !== initialIsSubscribed) {
            updateXCheckoutState({ addresses: { isSubscribed: initialIsSubscribed } });
        }
    }, [initialIsSubscribed]);

    const styles = mergeToNew(billingAddressStyles, extendedStyles);

    const selectAddressClickHandler = () => {
        theme.addressBook?.useOverlay
            ? setAddressBookOverlayIsOpen({ isOpen: true, isBillTo: true })
            : setIsCustomerSelectorModalOpen(true);
    };

    const customerSelectorModalCloseHandler = () => setIsCustomerSelectorModalOpen(false);

    const selectCustomerHandler = (billTo: BillToModel) => {
        customerSelectorModalCloseHandler();
        onChange(billTo, false);
    };

    const editCustomerHandler = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, customer: BillToModel) => {
        event.preventDefault();
        setAddressBeingEdited(customer);
    };

    const editAddressModalCloseHandler = () => {
        setIsEditAddressModalOpen(false);
        setAddressBeingEdited(undefined);
    };

    const editAddressFormSubmitHandler = (_: React.FormEvent<HTMLFormElement>, address: BillToModel) => {
        onChange(address, true);
        editAddressModalCloseHandler();
        customerSelectorModalCloseHandler();
        toaster.addToast({
            messageType: "success",
            body: translate("Address updated."),
        });
    };

    // XNG-Change
    const handleSaveBillingAddress = async () => {
        context.formSubmitAttempted = true;
        const isValidBillTo = xValidateOnSubmit(BillingFormId)?.length === 0;

        // validate billing address
        const validators: (() => Promise<boolean>)[] = [];
        const validator = context.validators.billingAddress;
        if (validator) {
            validators.push(validator);
        }

        const results = await Promise.all(validators.map(v => v()));

        if (!results.every(o => o) || !isValidBillTo) {
            scrollToFirstFormError();
            return;
        }

        onChange(billingAddressFormState || address, true);
    };

    // XNG-Change
    const handleChangeAddress = () => {
        updateXCheckoutState({ addresses: { billToEditMode: true } });
    };

    const handleIsSubscribedCheckbox = () => {
        setInitialIsSubscribed(!initialIsSubscribed);
    };

    return (
        <>
            <GridContainer {...styles.container} data-test-selector="checkoutShipping_billingAddress">
                {/* XNG-Change matching classic */}
                {/* <GridItem {...styles.headingGridItem}>
                    {billToEditMode &&
                        !currentUserIsGuest &&
                        cart?.status !== "QuoteProposed" &&
                        !(cart?.status === "Cart" && cart?.type === "Job") && (
                            <Link
                                {...styles.selectSavedAddressLink}
                                type="button"
                                onClick={selectAddressClickHandler}
                                data-test-selector="checkoutShipping_selectSavedAddress"
                            >
                                {translate("Select Saved Address")}
                                <VisuallyHidden>{translate("For Billing Address")}</VisuallyHidden>
                            </Link>
                        )}
                </GridItem> */}
                <GridItem {...styles.addressDisplayAndFormGridItem}>
                    {/* XNG-Change: currentUserIsGuest */}
                    {isBillingAddressUpdateRequired || billToEditMode ? (
                        <>
                            {addressFieldDisplayCollection && countries && (
                                <>
                                    <BillingAddressForm
                                        countries={countries}
                                        fieldDisplay={addressFieldDisplayCollection}
                                        extendedStyles={styles.addressForm}
                                        {...{ displayRequiredLabel: true }}
                                    />
                                    {/* XNG-Change */}
                                    {cart?.showNewsletterSignup && account && (
                                        <CheckboxGroup {...styles.manageSubscriptionsCheckboxGroup}>
                                            <Checkbox
                                                {...styles.manageSubscriptionsCheckbox}
                                                onChange={handleIsSubscribedCheckbox}
                                                checked={initialIsSubscribed}
                                                data-test-selector="accountSettings_subscribed"
                                            >
                                                {siteMessage("Address_SignMeUp")}
                                            </Checkbox>
                                        </CheckboxGroup>
                                    )}
                                    <StyledWrapper {...styles.saveWrapper}>
                                        <Button
                                            data-test-selector="saveBillingAddressButton"
                                            {...styles.saveButton}
                                            onClick={handleSaveBillingAddress}
                                        >
                                            {translate("Save & Continue")}
                                        </Button>
                                    </StyledWrapper>
                                </>
                            )}
                        </>
                    ) : (
                        <>
                            <AddressInfoDisplay
                                {...address}
                                state={address.state?.abbreviation}
                                country={address.country?.abbreviation}
                                extendedStyles={styles.addressDisplay}
                            />
                            <Link {...styles.change} onClick={handleChangeAddress}>
                                {translate("Change")}
                            </Link>
                        </>
                    )}
                </GridItem>
            </GridContainer>
            <Modal
                {...styles.addressBookModal}
                headline={translate("Address Book")}
                isOpen={isCustomerSelectorModalOpen}
                handleClose={customerSelectorModalCloseHandler}
                data-test-selector="checkoutShipping_addressModal"
            >
                <BillToSelector
                    currentBillTo={cartBillTo}
                    onSelect={selectCustomerHandler}
                    onEdit={editCustomerHandler}
                    extendedStyles={styles.billToSelector}
                />
                <StyledWrapper {...styles.addressBookModalButtonsWrapper}>
                    <Button
                        {...styles.addressBookModalCancelButton}
                        onClick={customerSelectorModalCloseHandler}
                        data-test-selector="addressModal_cancel"
                    >
                        {translate("Cancel")}
                    </Button>
                </StyledWrapper>
            </Modal>
            {addressBeingEdited && (
                <Modal
                    {...styles.addressBookModal}
                    headline={translate("Edit Address")}
                    isOpen={isEditAddressModalOpen}
                    handleClose={editAddressModalCloseHandler}
                    data-test-selector="checkoutShipping_editAddressModal"
                >
                    <CustomerAddressForm
                        address={addressBeingEdited}
                        countries={countries}
                        addressFieldDisplayCollection={addressFieldDisplayCollection}
                        onSubmit={editAddressFormSubmitHandler}
                        saveButtonTextOverride={translate("Save & Apply")}
                        onCancel={editAddressModalCloseHandler}
                        extendedStyles={styles.editAddressForm}
                    />
                </Modal>
            )}
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withToaster(withTheme(BillingAddress)));
